import { useEffect, useState } from 'react';
import type { ResourceBreak } from '../types';

function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

interface BreakTimerDisplayProps {
  activeBreak: ResourceBreak;
  className?: string;
  compact?: boolean;
}

export default function BreakTimerDisplay({ activeBreak, className = '', compact = false }: BreakTimerDisplayProps) {
  const [label, setLabel] = useState('--:--');

  useEffect(() => {
    const tick = () => {
      const sinceMs = new Date(activeBreak.since).getTime();
      if (activeBreak.timerMode === 'countdown' && activeBreak.endsAt) {
        const remaining = Math.floor((new Date(activeBreak.endsAt).getTime() - Date.now()) / 1000);
        setLabel(formatDuration(remaining));
      } else {
        const elapsed = Math.floor((Date.now() - sinceMs) / 1000);
        setLabel(formatDuration(elapsed));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeBreak]);

  const isCountdown = activeBreak.timerMode === 'countdown';
  const isExpired =
    isCountdown &&
    activeBreak.endsAt &&
    new Date(activeBreak.endsAt).getTime() <= Date.now();

  return (
    <span
      className={`font-mono font-bold tabular-nums ${isExpired ? 'text-red-400' : 'text-purple-300'} ${className}`}
      title={isCountdown ? 'Break countdown' : 'Break elapsed'}
    >
      {!compact && (isCountdown ? '↓ ' : '↑ ')}
      {label}
      {isExpired && !compact ? ' · due' : ''}
    </span>
  );
}
